/**
 * 자재 페이지 — 엑셀(.xlsx/.xls) 업로드 → 미리보기 → 일괄 등록 흐름의 순수 로직.
 *
 * 자재는 네트워크 스프레드시트와 달리 **고정 스키마**(name/quantity/vendor/category/unit/note)이므로
 * 엑셀 헤더를 우리 필드로 매핑해야 한다. 흐름:
 *   1. `autoMapHeaders(headers)` — 엑셀 첫 행 헤더를 자동 매핑.
 *   2. 사용자가 다이얼로그에서 미스매치 헤더를 드롭다운으로 수동 매핑.
 *   3. `buildPayloads(grid, mapping)` — 매핑이 적용된 행을 검증해 등록 가능한 payload 와 오류 목록을 분리.
 *
 * 엑셀 파싱(`parseExcelWorkbook`, `extractSheetAsGrid`)은 `excelImport.ts` 공용 유틸 재사용.
 */

import { CreateSiteMaterialRequest } from '../../services/SiteMaterialService';
import { SheetGrid } from './excelImport';

export { parseExcelWorkbook, extractSheetAsGrid } from './excelImport';
export type { ParsedWorkbook, SheetGrid } from './excelImport';

/**
 * 자재 필드 — 엑셀 헤더가 매핑될 수 있는 대상.
 * 'ignore' 는 사용자가 명시적으로 무시 처리한 헤더.
 */
export type MaterialField =
  | 'name'
  | 'quantity'
  | 'vendor'
  | 'category'
  | 'unit'
  | 'note'
  | 'ignore';

/** 각 필드의 한글 라벨 — UI 드롭다운/오류 메시지에 사용. */
export const MATERIAL_FIELD_LABELS: Record<MaterialField, string> = {
  name: '이름',
  quantity: '수량',
  vendor: '제품업체',
  category: '분류',
  unit: '단위',
  note: '비고',
  ignore: '무시',
};

/**
 * 자동 매핑 사전 — 엑셀 헤더(정규화 후) → 자재 필드.
 * 한국어 라벨 + 자주 쓰이는 영어 약어 모두 커버. 정규화는 trim + lowercase.
 */
const HEADER_ALIASES: Record<string, MaterialField> = {
  // name
  이름: 'name',
  자재명: 'name',
  자재이름: 'name',
  품명: 'name',
  제품명: 'name',
  name: 'name',
  item: 'name',
  // quantity
  수량: 'quantity',
  갯수: 'quantity',
  개수: 'quantity',
  quantity: 'quantity',
  qty: 'quantity',
  count: 'quantity',
  // vendor
  제품업체: 'vendor',
  업체: 'vendor',
  제조사: 'vendor',
  제조업체: 'vendor',
  공급사: 'vendor',
  공급업체: 'vendor',
  vendor: 'vendor',
  manufacturer: 'vendor',
  maker: 'vendor',
  brand: 'vendor',
  // category
  분류: 'category',
  카테고리: 'category',
  종류: 'category',
  유형: 'category',
  category: 'category',
  type: 'category',
  // unit
  단위: 'unit',
  unit: 'unit',
  // note
  비고: 'note',
  메모: 'note',
  설명: 'note',
  note: 'note',
  remark: 'note',
  remarks: 'note',
  description: 'note',
};

function normalizeHeader(h: string): string {
  return h.replace(/\s+/g, '').toLowerCase();
}

/**
 * 엑셀 헤더를 자재 필드로 자동 매핑.
 *
 *  - 인덱스 i 번째 헤더가 어떤 필드에 매핑됐는지 반환(매칭 안 된 헤더는 'ignore').
 *  - 동일 필드가 여러 헤더에 매핑되는 경우 **먼저 등장한 헤더만** 채택, 이후는 'ignore'.
 *    (사용자가 미리보기에서 원하면 다시 바꿀 수 있음.)
 */
export function autoMapHeaders(headers: string[]): MaterialField[] {
  const used = new Set<MaterialField>();
  const result: MaterialField[] = [];
  for (const h of headers) {
    const key = normalizeHeader(h);
    const candidate = HEADER_ALIASES[key];
    if (candidate && !used.has(candidate)) {
      used.add(candidate);
      result.push(candidate);
    } else {
      result.push('ignore');
    }
  }
  return result;
}

/** 매핑이 모든 필수 필드(name/quantity/vendor)를 포함하는지. */
export function hasRequiredMapping(mapping: MaterialField[]): boolean {
  return (
    mapping.includes('name') &&
    mapping.includes('quantity') &&
    mapping.includes('vendor')
  );
}

/** 매핑에서 동일 필드가 중복으로 선택된 헤더 인덱스 모음 (UI 경고용). */
export function findDuplicateFieldHeaders(
  mapping: MaterialField[],
): MaterialField[] {
  const counts = new Map<MaterialField, number>();
  for (const f of mapping) {
    if (f === 'ignore') continue;
    counts.set(f, (counts.get(f) ?? 0) + 1);
  }
  const dups: MaterialField[] = [];
  counts.forEach((cnt, field) => {
    if (cnt > 1) dups.push(field);
  });
  return dups;
}

export interface MaterialRowValidationError {
  /** 엑셀 데이터행 인덱스 (0-base, 헤더 제외) */
  rowIndex: number;
  /** 사람이 읽을 메시지 */
  message: string;
}

export interface BuildPayloadsResult {
  /** 검증 통과 → 백엔드에 보낼 수 있는 payload */
  payloads: CreateSiteMaterialRequest[];
  /** 검증 실패한 행 — UI 에서 표시 */
  errors: MaterialRowValidationError[];
  /** 매핑 자체가 불완전(필수 필드 미매핑)이면 채워짐 */
  mappingError: string | null;
}

/**
 * 매핑된 그리드를 등록 가능한 payload 와 오류로 분리.
 *
 *  - 매핑이 name/quantity/vendor 모두 포함하지 않으면 `mappingError` 만 채우고 빈 결과.
 *  - 각 행 검증: name·vendor 가 비어있으면 오류, quantity 가 정수 아니거나 음수면 오류.
 *  - 검증 통과한 행만 payloads 에 들어간다.
 */
export function buildPayloads(
  grid: SheetGrid,
  mapping: MaterialField[],
): BuildPayloadsResult {
  if (!hasRequiredMapping(mapping)) {
    const missing: string[] = [];
    if (!mapping.includes('name')) missing.push('이름');
    if (!mapping.includes('quantity')) missing.push('수량');
    if (!mapping.includes('vendor')) missing.push('제품업체');
    return {
      payloads: [],
      errors: [],
      mappingError: `필수 컬럼 매핑이 비어있습니다: ${missing.join(', ')}`,
    };
  }

  const nameIdx = mapping.indexOf('name');
  const qtyIdx = mapping.indexOf('quantity');
  const vendorIdx = mapping.indexOf('vendor');
  const categoryIdx = mapping.indexOf('category');
  const unitIdx = mapping.indexOf('unit');
  const noteIdx = mapping.indexOf('note');

  const payloads: CreateSiteMaterialRequest[] = [];
  const errors: MaterialRowValidationError[] = [];

  grid.dataRows.forEach((row, rowIndex) => {
    const name = (row[nameIdx] ?? '').trim();
    const vendor = (row[vendorIdx] ?? '').trim();
    const qtyRaw = (row[qtyIdx] ?? '').trim();

    if (!name) {
      errors.push({ rowIndex, message: '이름이 비어있습니다' });
      return;
    }
    if (!vendor) {
      errors.push({ rowIndex, message: '제품업체가 비어있습니다' });
      return;
    }
    // 엑셀에서 수치가 '4.0' 같이 들어올 수 있어 parseFloat 후 정수 검사.
    const qtyNum = qtyRaw === '' ? NaN : Number(qtyRaw);
    if (!Number.isFinite(qtyNum) || qtyNum < 0 || !Number.isInteger(qtyNum)) {
      errors.push({
        rowIndex,
        message: `수량이 0 이상 정수가 아닙니다: "${qtyRaw}"`,
      });
      return;
    }

    payloads.push({
      name,
      quantity: qtyNum,
      vendor,
      category: pickOptional(row, categoryIdx),
      unit: pickOptional(row, unitIdx),
      note: pickOptional(row, noteIdx),
    });
  });

  return { payloads, errors, mappingError: null };
}

function pickOptional(row: string[], idx: number): string | undefined {
  if (idx < 0) return undefined;
  const v = (row[idx] ?? '').trim();
  return v === '' ? undefined : v;
}
